/**
 * API client — authenticated requests to the WayAI backend
 */

import type {
  HubAsCodePayload,
  CiDiffResponse,
  CiLookupResponse,
  CiSyncResponse,
  CiOrganizationsResponse,
  CiCreateOrganizationResponse,
  CiHubListResponse,
  CiSyncMcpResponse,
  SyncSkillsResponse,
  OrgResourcesPayload,
  CiOrgResourcesPushResponse,
} from './types.js';
import type { PacingConfig } from '@wayai/core';
import type {
  AnalyticsSqlBody,
  AnalyticsSqlResponse,
  AnalyticsSqlSchemaResponse,
  AnalyticsSqlTable,
  AnalyticsStructuredQueryResponse,
  AuthMeResponse,
  DataExplorerDebugDoType,
  DataExplorerDebugAnalyticsTable,
  DataExplorerDebugObservabilityResponse,
  DeleteUserConversationsBody,
  DeleteUserConversationsResponse,
  AnnotateConversationResponse,
  ObservabilityListResponse,
  ObservabilityResponse,
  EvalsSqlBody,
  EvalsSqlResponse,
  EvalsSqlSchemaResponse,
  EvalScenarioSelection,
  StructuredQueryBody,
  AdminReportListResponse,
  AdminReportDetail,
  TransitionReportResponse,
  GroupReportsResponse,
  UngroupReportResponse,
  HoldReportResponse,
  TransitionReportBody,
  EditReportBody,
  EditReportResponse,
  GetReportResponse,
  ReporterListResponse,
  ReporterTransitionResponse,
  ListOrgCredentialsResponse,
  ListConnectionsResponse,
  EditConnectionResponse,
  SetProductionCredentialResponse,
  GetHubDiffResponse,
  PublishHubResponse,
  SyncHubResponse,
  CreateOrgCredentialResponse,
  CreateOrgCredentialInput,
  UpdateOrgCredentialResponse,
  UpdateOrgCredentialInput,
  ListOrgTagsResponse,
  CreateBaseCredentialLinkResponse,
  ToolCallResponse,
  AdminNoticeListResponse,
  AdminNoticeResponse,
  CreateNoticeBody,
  AdminPeopleReportListQuery,
  AdminPeopleReportListResponse,
  AdminPeopleReportResponse,
  AdminPeopleMessageRemovalBody,
  AdminPeopleSuspendBody,
  AdminPeopleNoteBody,
  AdminPeopleModerationResponse,
  AdminPeopleUserResponse,
  AdminSkillResponse,
  AdminSandboxExecBody,
  AdminSandboxExecResponse,
  AdminHarnessMassDestroyInput,
  AdminHarnessMassDestroyResponse,
  AdminRepairTeamOrphansInput,
  AdminRepairTeamOrphansResponse,
  AdminRepairBranchingOrphansInput,
  AdminRepairBranchingOrphansResponse,
  AdminRepairLegacyOrgGrantsInput,
  AdminRepairLegacyOrgGrantsResponse,
  AdminConfigResponse,
  UpdatePlatformConfigInput,
  AdminCreateCommunityBody,
  AdminCreateCommunityResponse,
  AdminDestroyCommunityResponse,
  HubAlertsListResponse,
  MessageAttachment,
  GetConversationDetailResponse,
  GetConversationsResponse,
  RunSessionResponse,
  StopSessionResponse,
  EvalRunOutcomeKind,
  CallResponse,
  CreateEvalCallBody,
  CreateEvalCallResponse,
  EvalCallBody,
  EvalCallConversationQuery,
  EvalCallFinishBody,
  EvalCallFinishResponse,
  EvalCallRecordResponse,
  GetJourneyResponse,
  GetJourneysResponse,
} from '@wayai/core/contracts';
import {
  getConversationDetailResponse,
  getConversationsResponse,
  DATA_PROXY_PREFIX,
  DATA_PROXY_ORG_QUERY_PARAM,
  REKOR_V1_PREFIX,
} from '@wayai/core/contracts';
import { addApiBreadcrumb } from './sentry.js';
import { maskSecretsInMessage } from './mask-secrets.js';

/**
 * Bounded backoff (ms) for the transient-retry loop in `ApiClient.request`.
 * Three retries — enough to ride out a ClickHouse Cloud cold-start (whose wake
 * probe the first request already triggered) without hanging a CLI command.
 */
const RETRYABLE_BACKOFF_MS = [500, 1000, 2000] as const;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Whether an error response body carries the backend's `details.retryable: true`
 * flag — a safe-to-retry transient (today only `CLICKHOUSE_COLD` cold-start).
 * Best-effort JSON parse: a non-JSON / unshaped body is treated as not retryable.
 */
function isRetryableErrorBody(body: string): boolean {
  try {
    const parsed = JSON.parse(body) as { details?: { retryable?: unknown } };
    return parsed?.details?.retryable === true;
  } catch {
    return false;
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: string;
  /**
   * The request path that failed. Retained because a body alone cannot say
   * WHICH surface answered: WayAI and the Data surface both emit 404s, and
   * telling them apart by body shape misreads one for the other.
   */
  readonly path: string;

  constructor(method: string, path: string, status: number, body: string) {
    // Redact token-shaped secrets the backend may have echoed into the error
    // body, since both `.message` and `.body` are printed to the console by many
    // commands (and `.message` is reported to Sentry). Benign bodies pass
    // through unchanged. Done at the single throw site so every consumer is safe.
    const safeBody = maskSecretsInMessage(body);
    super(`API request failed: ${method} ${path} (${status}): ${safeBody}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = safeBody;
    this.path = path;
  }

  /** True when the status code is a 4xx client error (expected user-facing condition, not a bug). */
  get isExpected(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  /**
   * True when the backend flagged this as a safe-to-retry transient via
   * `details.retryable === true` (today: `CLICKHOUSE_COLD` cold-start). The
   * client retries these inline (see `request`); if the retry budget is
   * exhausted and one still surfaces, this lets `shouldReportToSentry` keep it
   * out of Sentry and lets command surfaces render a "warming up" hint rather
   * than a crash — mirroring the backend's `SENTRY_SKIP_CODES` (gh #2883).
   */
  get isRetryable(): boolean {
    return isRetryableErrorBody(this.body);
  }
}

/**
 * Where a Data-surface path lands on the WayAI backend.
 *
 * The CLI never talks to the Data Worker directly: it authenticates once as
 * WayAI, and the backend forwards over a service binding that attests the org.
 * The proxy STRIPS its own
 * prefix and PREPENDS `/v1`, so a command's `/v1/bases` becomes `/api/data/bases`
 * on the wire and `/v1/bases` again on the far side. Sending the `/v1` through
 * unchanged would forward `/v1/v1/bases` and 404 every request.
 *
 * Both constants are imported rather than spelled here: the backend handler keys
 * off the same two, so a rename moves both sides together instead of silently
 * 404ing the whole surface.
 */
export function toDataProxyPath(v1Path: string): string {
  if (!v1Path.startsWith(`${REKOR_V1_PREFIX}/`)) {
    throw new Error(`Data path must start with ${REKOR_V1_PREFIX}/: ${v1Path}`);
  }
  return `${DATA_PROXY_PREFIX}${v1Path.slice(REKOR_V1_PREFIX.length)}`;
}

/**
 * Append the org SELECTOR to a Data path. The backend validates it against the
 * caller's grants and strips it before forwarding — it is WayAI's input, never
 * one Rekor sees.
 */
export function withOrgSelector(path: string, orgId?: string): string {
  if (!orgId) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${DATA_PROXY_ORG_QUERY_PARAM}=${encodeURIComponent(orgId)}`;
}

/** Cursor-pagination meta on a Data-surface list response. */
export interface DataListMeta {
  has_more?: boolean;
  cursor?: string;
}

/** The `{ data, meta }` response envelope every Data-surface route returns. */
export interface DataEnvelope<T> {
  data?: T;
  meta?: DataListMeta;
}

/**
 * The human-facing sentence for a failed Data-surface request, or null when
 * there is none to give.
 *
 * `ApiError.message` is the generic transport line (`API request failed: GET
 * /api/data/v1/bases (404): {…}`); this returns either the surface's own
 * `error.message` or, for the one case that message cannot exist, an
 * explanation. Consumed by `friendlyHint`, so every command inherits it without
 * calling this itself.
 */
export function dataErrorMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  let parsed: { error?: unknown } | undefined;
  try {
    parsed = JSON.parse(err.body) as { error?: unknown };
  } catch {
    return null;
  }

  // Scoped to the Data prefix, NOT inferred from the body. WayAI's own 404 body
  // is `{ error: 'NOT_FOUND', message: … }` — the same shape — so matching on
  // shape alone told every user whose hub or connection lookup 404'd to go
  // update their CLI. Only a Data-prefixed path can be a missing Data proxy.
  if (err.status === 404 && err.path.startsWith(DATA_PROXY_PREFIX) && typeof parsed?.error === 'string') {
    return (
      'The Data surface is not available on this backend. ' +
      'Update the CLI, or point it at a backend that serves it.'
    );
  }

  const message = (parsed?.error as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' && message ? message : null;
}

export interface ApiClientOptions {
  apiUrl: string;
  accessToken: string;
  /**
   * Optional re-auth hook. When a request comes back 401, the client invokes
   * this to obtain a fresh token, swaps it in, and retries the request exactly
   * once (mirroring the frontend `fetchWithAuth` single-refresh-then-retry).
   *
   * Long-running commands whose lifetime can exceed a short-lived OAuth JWT —
   * `run-eval`'s poll being the only one — pass `() => getAccessToken(config)`
   * so a token that rotates/expires mid-command self-heals instead of aborting
   * (gh #2605). Short-lived commands omit it and keep the throw-on-401 behavior.
   */
  onUnauthorized?: () => Promise<string>;
}

/**
 * Options for the hub conversation-index listing (`GET /api/conversations`).
 * Declared once and exported because the cross-hub fan-out relays the same bag
 * verbatim — a change here cannot pass typecheck while the fan-out keeps sending
 * the old shape.
 */
export interface ConversationListOptions {
  status?: string;
  limit?: number;
  offset?: number;
  /** Inclusive last-activity window (canonical ISO-8601 UTC) — see `listConversationsQuery`. */
  activityFrom?: string;
  activityTo?: string;
}

export class ApiClient {
  private apiUrl: string;
  private accessToken: string;
  private onUnauthorized?: () => Promise<string>;

  constructor(options: ApiClientOptions) {
    this.apiUrl = options.apiUrl;
    this.accessToken = options.accessToken;
    this.onUnauthorized = options.onUnauthorized;
  }

  async pull(hubId: string, organizationId?: string): Promise<HubAsCodePayload> {
    const qs = organizationId ? `?${new URLSearchParams({ organization_id: organizationId })}` : '';
    return this.request<HubAsCodePayload>('GET', `/api/ci/pull/${hubId}${qs}`);
  }

  async diff(hubId: string, config: HubAsCodePayload, direction: 'push' | 'pull' = 'push', organizationId?: string): Promise<CiDiffResponse> {
    return this.request<CiDiffResponse>('POST', '/api/ci/diff', {
      hub_id: hubId,
      config,
      direction,
      ...(organizationId && { organization_id: organizationId }),
    });
  }

  async push(hubId: string, config: HubAsCodePayload, organizationId?: string): Promise<CiSyncResponse> {
    return this.request<CiSyncResponse>('POST', '/api/ci/push', {
      hub_id: hubId,
      config,
      ...(organizationId && { organization_id: organizationId }),
    });
  }

  async sync(hubId: string, branchName: string, config: HubAsCodePayload, commitSha?: string): Promise<CiSyncResponse> {
    return this.request<CiSyncResponse>('POST', '/api/ci/sync', {
      hub_id: hubId,
      branch_name: branchName,
      config,
      commit_sha: commitSha,
    });
  }

  // Download a private operator skill (platform-admin gated) for local install.
  async adminSkillInstall(name: string): Promise<AdminSkillResponse> {
    return this.request<AdminSkillResponse>('GET', `/api/admin/skills/${encodeURIComponent(name)}`);
  }

  // Run a shell command in a fresh E2B sandbox (platform-admin gated — the
  // harness-agents PR 1.5 isolation surface). Never carries the E2B key.
  async adminSandboxExec(body: AdminSandboxExecBody): Promise<AdminSandboxExecResponse> {
    return this.request<AdminSandboxExecResponse>('POST', '/api/admin/sandbox/exec', body);
  }

  // Reap every live harness sandbox / token / slot in scope (platform-admin gated
  // — the harness-agents PR 2.9b incident-response surface). Never carries tokens.
  async adminHarnessMassDestroy(body: AdminHarnessMassDestroyInput): Promise<AdminHarnessMassDestroyResponse> {
    return this.request<AdminHarnessMassDestroyResponse>('POST', '/api/admin/harness/mass-destroy', body);
  }

  // Purge dangling hub_team_id references left by a pre-cascade team deletion.
  // Used by `wayai admin hubs repair-team-orphans` (platform-admin gated).
  async adminRepairTeamOrphans(body: AdminRepairTeamOrphansInput): Promise<AdminRepairTeamOrphansResponse> {
    return this.request<AdminRepairTeamOrphansResponse>('POST', '/api/admin/hubs/repair-team-orphans', body);
  }

  // Report twin candidates, or delete named production rows no preview maps (#4577).
  // Used by `wayai admin hubs repair-branching-orphans` (platform-admin gated).
  async adminRepairBranchingOrphans(
    body: AdminRepairBranchingOrphansInput,
  ): Promise<AdminRepairBranchingOrphansResponse> {
    return this.request<AdminRepairBranchingOrphansResponse>('POST', '/api/admin/hubs/repair-branching-orphans', body);
  }

  // Repair the roster rows the pre-#3922 platform-admin add wrote (email in the
  // `user_id` column, `user_email` NULL) — each one an inert phantom seat.
  // Used by `wayai admin orgs repair-legacy-grants` (platform-admin gated).
  async adminRepairLegacyOrgGrants(body: AdminRepairLegacyOrgGrantsInput): Promise<AdminRepairLegacyOrgGrantsResponse> {
    return this.request<AdminRepairLegacyOrgGrantsResponse>('POST', '/api/admin/organizations/repair-legacy-grants', body);
  }

  // Read the platform config (platform-admin gated). Used by every `wayai admin <gate>
  // status` — grep `PlatformGate` in `commands/admin.ts` for the gates it serves.
  async adminGetConfig(): Promise<AdminConfigResponse> {
    return this.request<AdminConfigResponse>('GET', '/api/admin/config');
  }

  // Patch the platform config (platform-admin gated). Used by every `wayai admin
  // <gate> disable|enable` — grep `PlatformGate` in `commands/admin.ts` for the column each
  // gate flips.
  async adminUpdateConfig(body: UpdatePlatformConfigInput): Promise<AdminConfigResponse> {
    return this.request<AdminConfigResponse>('PATCH', '/api/admin/config', body);
  }

  // Create an org-owned community (platform-admin gated, gh #4872). Used by
  // `wayai admin communities create`.
  async adminCreateCommunity(body: AdminCreateCommunityBody): Promise<AdminCreateCommunityResponse> {
    return this.request<AdminCreateCommunityResponse>('POST', '/api/admin/communities', body);
  }

  // Destroy an org-owned community (platform-admin gated, gh #4872). Used by
  // `wayai admin communities destroy`.
  async adminDestroyCommunity(communityId: string): Promise<AdminDestroyCommunityResponse> {
    return this.request<AdminDestroyCommunityResponse>('DELETE', `/api/admin/communities/${encodeURIComponent(communityId)}`);
  }

  /**
   * Re-discover an MCP connection's tool/resource catalog (refreshes stale
   * input schemas that `push` leaves untouched). `connection` is the display
   * name or the connection UUID. With `{ check: true }` the server compares the
   * live catalog and returns drift WITHOUT writing anything (read-only).
   */
  async syncMcp(
    hubId: string,
    connection: string,
    organizationId?: string,
    opts?: { check?: boolean },
  ): Promise<CiSyncMcpResponse> {
    return this.request<CiSyncMcpResponse>('POST', '/api/ci/sync-mcp', {
      hub_id: hubId,
      connection,
      ...(organizationId && { organization_id: organizationId }),
      ...(opts?.check && { check: true }),
    });
  }

  async lookup(path: string, opts?: { organizationId?: string }): Promise<CiLookupResponse> {
    const params = new URLSearchParams({ path });
    if (opts?.organizationId) params.set('organization_id', opts.organizationId);
    return this.request<CiLookupResponse>('GET', `/api/ci/lookup?${params.toString()}`);
  }

  async organizations(): Promise<CiOrganizationsResponse> {
    return this.request<CiOrganizationsResponse>('GET', '/api/ci/organizations');
  }

  /** Create a new organization (the caller becomes its owner). */
  async createOrganization(name: string, region?: string): Promise<CiCreateOrganizationResponse> {
    return this.request<CiCreateOrganizationResponse>('POST', '/api/ci/organizations', {
      organization_name: name,
      ...(region ? { primary_region: region } : {}),
    });
  }

  // ---- Org-as-code (org-scoped resources) ----

  async pullOrgResources(orgId: string): Promise<OrgResourcesPayload> {
    return this.request<OrgResourcesPayload>('GET', `/api/ci/org-resources/${orgId}`);
  }

  async diffOrgResources(
    orgId: string,
    config: OrgResourcesPayload,
    direction: 'push' | 'pull' = 'push',
  ): Promise<CiDiffResponse> {
    return this.request<CiDiffResponse>('POST', '/api/ci/org-resources/diff', {
      organization_id: orgId,
      config,
      direction,
    });
  }

  async pushOrgResources(orgId: string, config: OrgResourcesPayload): Promise<CiOrgResourcesPushResponse> {
    return this.request<CiOrgResourcesPushResponse>('POST', '/api/ci/org-resources/push', {
      organization_id: orgId,
      config,
    });
  }

  async me(): Promise<AuthMeResponse> {
    return this.request<AuthMeResponse>('GET', '/api/auth/me');
  }

  async syncSkills(hubId: string, connectionId?: string): Promise<SyncSkillsResponse> {
    return this.request<SyncSkillsResponse>('POST', '/api/setup/resources/sync-skills', {
      hub_id: hubId,
      ...(connectionId && { connection_id: connectionId }),
    });
  }

  async listHubs(organizationId: string): Promise<CiHubListResponse> {
    const params = new URLSearchParams({ organization_id: organizationId });
    return this.request<CiHubListResponse>('GET', `/api/ci/hubs?${params.toString()}`);
  }

  async sendMessage(
    hubId: string,
    message: string | undefined,
    opts?: { conversationId?: string; attachments?: MessageAttachment[] },
  ): Promise<{
    conversation_id: string;
    hub_type: string;
    user_message_id: string;
    agent_reply: { message_id: string; message_text: string } | null;
    status: string;
    status_reason?: string;
  }> {
    return this.request('POST', '/api/conversations/send-message', {
      hub_id: hubId,
      // Omitted entirely when absent: the contract allows an attachment-only send,
      // but `message: undefined` would still fail its `.min(1)` if serialized.
      ...(message && { message }),
      ...(opts?.conversationId && { conversation_id: opts.conversationId }),
      ...(opts?.attachments?.length && { attachments: opts.attachments }),
    });
  }

  async deleteHistory(
    hubId: string,
    opts: { userId: string } & ({ mode: 'all' } | { mode: 'current'; conversationId: string }),
  ): Promise<DeleteUserConversationsResponse> {
    const body: DeleteUserConversationsBody = opts.mode === 'current'
      ? { hub_id: hubId, user_id: opts.userId, mode: 'current', conversation_id: opts.conversationId }
      : { hub_id: hubId, user_id: opts.userId, mode: 'all' };
    return this.request<DeleteUserConversationsResponse>('POST', '/api/conversations/delete-user-history', body);
  }

  async annotateConversation(
    hubId: string,
    conversationId: string,
    annotation: { key: string; value: string | number | boolean; type: 'numeric' | 'categorical' | 'text' },
  ): Promise<AnnotateConversationResponse> {
    return this.request<AnnotateConversationResponse>(
      'POST',
      `/api/conversations/${conversationId}/annotations`,
      { hub_id: hubId, ...annotation },
    );
  }

  async getConversations(
    hubId: string,
    opts?: ConversationListOptions,
  ): Promise<GetConversationsResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    if (opts?.status) params.set('status', opts.status);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    if (opts?.activityFrom) params.set('activity_from', opts.activityFrom);
    if (opts?.activityTo) params.set('activity_to', opts.activityTo);
    return getConversationsResponse.parse(
      await this.request('GET', `/api/conversations?${params.toString()}`),
    );
  }

  // ---------------------------------------------------------------------------
  // Evals
  // ---------------------------------------------------------------------------

  async getEvals(hubId: string, opts?: { enabled?: string }): Promise<{
    success: true;
    data: {
      evals: Array<{
        eval_id: string;
        eval_name: string;
        enabled: boolean;
        number_of_runs: number;
        responder_agent_id: string;
        scenario_set_id: string;
        hub_id: string;
        agent?: { agent_id: string; agent_name: string; agent_role: string } | null;
      }>;
      total_count: number;
    };
  }> {
    const params = new URLSearchParams({ hub_id: hubId });
    if (opts?.enabled) params.set('enabled', opts.enabled);
    return this.request('GET', `/api/evals?${params.toString()}`);
  }

  async getEvalSessions(hubId: string, opts?: { status?: string }): Promise<{
    success: true;
    data: {
      sessions: Array<{
        eval_session_id: string;
        session_name: string;
        session_status: string;
        hub_id: string;
        total_evals: number;
        total_runs: number;
        successful_runs: number;
        failed_runs: number;
        // Optional: a released binary may be pointed at an API predating them.
        assertion_failure_runs?: number;
        errored_runs?: number;
        invalid_runs?: number;
        started_at?: string;
        completed_at?: string;
        created_at: string;
      }>;
      total_count: number;
    };
  }> {
    const params = new URLSearchParams({ hub_id: hubId });
    if (opts?.status) params.set('status', opts.status);
    return this.request('GET', `/api/evals/sessions?${params.toString()}`);
  }

  async createEvalSession(
    hubId: string,
    sessionName: string,
    opts?: {
      scenarioSetId?: string;
      pacing?: PacingConfig;
      /** Ephemeral scenario subset for this session (gh #3748). */
      scenarioSelection?: EvalScenarioSelection;
      /** Run every turn as a call turn is written (plan §12.1). */
      callMode?: boolean;
    },
  ): Promise<{
    success: true;
    data: {
      session: {
        eval_session_id: string;
        session_name: string;
        session_status: string;
        hub_id: string;
      };
    };
    message: string;
  }> {
    // `session_config` carries every per-launch choice, so both keys build one
    // object — sending two `session_config`s would drop the first.
    const sessionConfig = {
      ...(opts?.pacing ? { pacing: opts.pacing } : {}),
      ...(opts?.scenarioSelection ? { scenario_selection: opts.scenarioSelection } : {}),
      ...(opts?.callMode ? { call_mode: true } : {}),
    };
    return this.request('POST', '/api/evals/sessions', {
      session: {
        hub_id: hubId,
        session_name: sessionName,
        // Binding the set at create time targets a specific set (multi-set hubs);
        // when omitted, the run endpoint resolves the hub's sole enabled set.
        ...(opts?.scenarioSetId ? { scenario_set_id: opts.scenarioSetId } : {}),
        // Run pacing — omitted lets the backend apply its default (balanced).
        // The selection rides here too, so the session row IS the audit record
        // of which scenarios and repetitions it scored.
        ...(Object.keys(sessionConfig).length > 0 ? { session_config: sessionConfig } : {}),
      },
    });
  }

  async runEvalSession(hubId: string, sessionId: string, fixture?: string): Promise<RunSessionResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    if (fixture) params.set('fixture', fixture);
    return this.request('POST', `/api/evals/sessions/${sessionId}/run?${params.toString()}`);
  }

  /** Cancel a running eval session. A session that already finished is left unchanged. */
  async stopEvalSession(hubId: string, sessionId: string): Promise<StopSessionResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request('POST', `/api/evals/sessions/${sessionId}/stop?${params.toString()}`);
  }

  async getEvalSessionDetails(hubId: string, sessionId: string): Promise<{
    success: true;
    data: {
      session: {
        eval_session_id: string;
        session_name: string;
        session_status: string;
        started_at?: string;
        completed_at?: string;
      };
      results: Array<{
        eval_session_result_id: string;
        eval_id: string;
        total_runs: number;
        successful_runs: number;
        failed_runs: number;
        // Optional: a released binary may be pointed at an API predating them.
        assertion_failure_runs?: number;
        errored_runs?: number;
        invalid_runs?: number;
        pending_runs: number;
        running_runs: number;
        success_rate: number;
        avg_execution_time_ms?: number;
        aggregated_scores?: Record<string, { avg: number; min: number; max: number }>;
        eval_status: string;
        eval?: { eval_id: string; eval_name: string; responder_agent_id: string } | null;
      }>;
    };
  }> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request('GET', `/api/evals/sessions/${sessionId}?${params.toString()}`);
  }

  async getEvalSessionRuns(hubId: string, sessionId: string, opts?: { evalId?: string; limit?: number }): Promise<{
    success: true;
    data: {
      runs: Array<{
        eval_session_run_id: string;
        eval_id: string;
        conversation_id?: string;
        // Per-turn message ids in the eval conversation, so `eval-results --runs`
        // can point straight at a turn's LLM observability. Absent on pre-capture
        // rows / runs that never produced the respective turn.
        response_message_id?: string;
        evaluator_message_id?: string;
        run_number: number;
        eval_response: {
          role: string;
          content: string;
          tool_calls?: ToolCallResponse[];
        } | null;
        // Frozen expected response (scenario snapshot) for expected-vs-actual
        // diffs on a failing run (gh #2813). Absent when none was captured.
        expected_response?: {
          role: string;
          content: string | null;
          tool_calls?: ToolCallResponse[];
        };
        response_match: boolean | null;
        // Why a terminal run ended as it did — labels a non-passing run ERROR /
        // INVALID / FAIL (gh #3756). Absent while non-terminal or pre-taxonomy.
        outcome_kind?: EvalRunOutcomeKind;
        response_comment?: string;
        // Present when the run itself errored (agent threw / timed out); rendered
        // by `printFailingRuns` (gh #2813). Omitted on a clean run.
        error_message?: string;
        execution_time_ms?: number;
        run_status: string;
        scores?: Record<string, number>;
        eval?: { eval_id: string; eval_name: string; responder_agent_id: string } | null;
      }>;
      total_count: number;
      session_id: string;
    };
  }> {
    const params = new URLSearchParams({ hub_id: hubId });
    if (opts?.evalId) params.set('eval_id', opts.evalId);
    if (opts?.limit) params.set('limit', String(opts.limit));
    return this.request('GET', `/api/evals/sessions/${sessionId}/runs?${params.toString()}`);
  }

  /** Delete a single eval session (cascades runs/results/conversations server-side). */
  async deleteEvalSession(hubId: string, sessionId: string): Promise<{
    success: true;
    message: string;
    data?: { deleted_conversations: number };
  }> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request('DELETE', `/api/evals/sessions/${sessionId}?${params.toString()}`);
  }

  /** Delete ALL eval sessions for a hub (each cascades server-side). */
  async deleteAllEvalSessions(hubId: string): Promise<{
    success: true;
    message: string;
    data: { deleted_sessions: number; deleted_conversations: number };
  }> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request('DELETE', `/api/evals/sessions?${params.toString()}`);
  }

  async listScenarioSets(hubId: string): Promise<{
    success: true;
    data: {
      scenario_sets: Array<{
        scenario_set_id: string;
        set_name: string;
        description?: string | null;
        enabled: boolean;
      }>;
      total_count: number;
    };
  }> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request('GET', `/api/evals/scenario-sets?${params.toString()}`);
  }

  async createScenarioSet(hubId: string, setName: string): Promise<{
    success: true;
    data: {
      scenario_set: {
        scenario_set_id: string;
        set_name: string;
      };
    };
  }> {
    return this.request('POST', '/api/evals/scenario-sets', {
      hub_id: hubId,
      set_name: setName,
    });
  }

  /** The hub's eval journeys, newest first. */
  async listJourneys(hubId: string): Promise<GetJourneysResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request('GET', `/api/evals/journeys?${params.toString()}`);
  }

  async getJourney(hubId: string, journeyId: string): Promise<GetJourneyResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request('GET', `/api/evals/journeys/${encodeURIComponent(journeyId)}?${params.toString()}`);
  }

  // ---------------------------------------------------------------------------
  // Eval calls (`/api/evals/calls`, gh #4418) — the call-eval runner's calls
  // ---------------------------------------------------------------------------

  /** Place an eval call for a journey whose responder is a `pilot_voice` agent. */
  async createEvalCall(body: CreateEvalCallBody): Promise<CreateEvalCallResponse> {
    return this.request('POST', '/api/evals/calls', body);
  }

  async readyEvalCall(callId: string, hubId: string): Promise<CallResponse> {
    return this.request('POST', `/api/evals/calls/${encodeURIComponent(callId)}/ready`, { hub_id: hubId } satisfies EvalCallBody);
  }

  async hangupEvalCall(callId: string, hubId: string): Promise<CallResponse> {
    return this.request('POST', `/api/evals/calls/${encodeURIComponent(callId)}/hangup`, { hub_id: hubId } satisfies EvalCallBody);
  }

  /** The call's record, for scoring: its utterances, call turns and charge. */
  async getEvalCallRecord(callId: string, hubId: string, conversationId: string): Promise<EvalCallRecordResponse> {
    const query = { hub_id: hubId, conversation_id: conversationId } satisfies EvalCallConversationQuery;
    return this.request('GET', `/api/evals/calls/${encodeURIComponent(callId)}?${new URLSearchParams(query).toString()}`);
  }

  /** End the call if live, close its eval conversation and remove its synthetic caller. */
  async finishEvalCall(callId: string, hubId: string, conversationId: string): Promise<EvalCallFinishResponse> {
    const body = { hub_id: hubId, conversation_id: conversationId } satisfies EvalCallFinishBody;
    return this.request('POST', `/api/evals/calls/${encodeURIComponent(callId)}/finish`, body);
  }

  async runEvalsSql(body: EvalsSqlBody): Promise<EvalsSqlResponse> {
    return this.request<EvalsSqlResponse>('POST', '/api/evals/sql', body);
  }

  async getEvalsSqlSchema(hubId: string): Promise<EvalsSqlSchemaResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request<EvalsSqlSchemaResponse>('GET', `/api/evals/sql/schema?${params.toString()}`);
  }

  async createScenarioFromConversation(input: {
    hub_id: string;
    conversation_id: string;
    scenario_set_id: string;
    scenario_name: string;
    evaluator_instructions?: string;
  }): Promise<{
    success: true;
    data: {
      eval: {
        eval_id: string;
        eval_name: string;
        scenario_set_id: string;
        responder_agent_id: string;
        message_text: { role: string; content?: string | null; tool_calls?: unknown };
        message_expected_response: { role: string; content?: string | null; tool_calls?: unknown };
        message_history: Array<{ role: string; content?: string | null; tool_calls?: unknown }>;
        evaluator_instructions?: string;
        number_of_runs: number;
        enabled: boolean;
      };
    };
    message: string;
  }> {
    return this.request('POST', '/api/evals/scenarios/from-conversation', input);
  }

  async createJourneyFromConversation(input: {
    hub_id: string;
    conversation_id: string;
    journey_name: string;
    evaluator_instructions?: string;
  }): Promise<{
    success: true;
    data: {
      journey: {
        journey_id: string;
        journey_name: string;
        scenario_set_id: string;
        responder_agent_id: string;
        transcript: Array<{ role: string; content?: string | null; tool_calls?: unknown; tool_call_id?: string; step_id?: string }>;
        evaluator_instructions?: string | null;
        enabled: boolean;
      };
    };
    message: string;
  }> {
    return this.request('POST', '/api/evals/journeys/from-conversation', input);
  }

  // ---------------------------------------------------------------------------
  // Analytics
  // ---------------------------------------------------------------------------

  async getAnalyticsVariables(hubId: string): Promise<{
    success: true;
    data: {
      categories: Array<{
        category: string;
        label: string;
        variableCount: number;
      }>;
      variablesByCategory: Record<string, Array<{
        variable_id: string;
        variable_name: string;
        variable_description?: string;
        variable_label?: string;
        variable_type: 'numeric' | 'categorical' | 'text';
        variable_origin: string;
        variable_category: string;
        variable_enum: unknown[];
        pin: boolean;
      }>>;
    };
  }> {
    return this.request('GET', `/api/analytics/hubs/${hubId}/variables`);
  }

  async getAnalyticsData(body: {
    hub_id: string;
    selected_variables: string[];
    time_interval: { start: string; end: string };
    periodicity: string;
    trend_analysis: boolean;
    include_summary: boolean;
    filters?: Array<{ variable_id: string; filter_type: string; filter_value: string | number }>;
  }): Promise<{
    analytics: Array<{
      variable: string;
      variable_id: string;
      type: 'numeric' | 'categorical' | 'text';
      data?: Array<{ period: string; value: number | string | Record<string, number> }>;
      metrics?: { average: number; sum: number; minimum: number; maximum: number; count?: number; median?: number };
      distribution?: Record<string, number>;
    }>;
    summary?: {
      total_conversations: number;
      ai_only_rate: number;
      avg_response_time: number;
      assistant_response_time: number;
      support_response_time: number;
      user_response_time: number;
    };
  }> {
    return this.request('POST', '/api/analytics/data', body);
  }

  async runAnalyticsQuery(body: StructuredQueryBody): Promise<AnalyticsStructuredQueryResponse> {
    return this.request<AnalyticsStructuredQueryResponse>('POST', '/api/analytics/query', body);
  }

  async runAnalyticsSql(body: AnalyticsSqlBody): Promise<AnalyticsSqlResponse> {
    return this.request<AnalyticsSqlResponse>('POST', '/api/analytics/sql', body);
  }

  async getAnalyticsSqlSchema(hubId: string, table?: AnalyticsSqlTable): Promise<AnalyticsSqlSchemaResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    if (table) params.set('table', table);
    return this.request<AnalyticsSqlSchemaResponse>('GET', `/api/analytics/sql/schema?${params.toString()}`);
  }

  async getAnalyticsConversations(body: {
    hub_id: string;
    time_interval?: { start: string; end: string };
    filters?: Array<{ variable_id: string; filter_type: string; filter_value: string | number }>;
    limit: number;
    offset: number;
  }): Promise<{
    success: true;
    conversations: Array<{
      conversation_id: string;
      created_at?: string;
      updated_at?: string;
      message_count?: number;
      participant_name?: string;
      participant_phone?: string;
    }>;
    total: number;
    offset: number;
    limit: number;
    // Set when ClickHouse was cold/transiently unavailable and the listing is a
    // provisional empty response rather than a real "no conversations" result.
    // The CLI surfaces this distinctly instead of rendering empty (gh #2452).
    cold?: boolean;
  }> {
    return this.request('POST', '/api/analytics/conversations', body);
  }

  async getConversation(
    hubId: string,
    conversationId: string,
    opts?: { limit?: number; offset?: number },
  ): Promise<GetConversationDetailResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    return getConversationDetailResponse.parse(
      await this.request(
        'GET',
        `/api/conversations/${conversationId}/detail?${params.toString()}`,
      ),
    );
  }

  // ---------------------------------------------------------------------------
  // Setup: Organization Credentials, Hubs
  // ---------------------------------------------------------------------------

  async createOrgCredential(body: {
    organization_id: string;
    credential_display_name: string;
    authentication_type: 'api_key' | 'bearer' | 'basic_auth';
    credential_description?: string;
    api_key?: string;
    access_token?: string;
    username?: string;
    password?: string;
    tags?: string[];
    environment?: CreateOrgCredentialInput['environment'];
  }): Promise<CreateOrgCredentialResponse> {
    return this.request('POST', '/api/setup/organization-credentials', body);
  }

  async listOrgCredentials(organizationId: string): Promise<ListOrgCredentialsResponse> {
    const qs = new URLSearchParams({ organization_id: organizationId });
    return this.request('GET', `/api/setup/organization-credentials?${qs.toString()}`);
  }

  async listConnections(hubId: string): Promise<ListConnectionsResponse> {
    const qs = new URLSearchParams({ hub_id: hubId });
    return this.request('GET', `/api/setup/connections?${qs.toString()}`);
  }

  /**
   * Active operational alerts (Status & Notices) for a hub — the same runtime
   * connection/credential-health surface the web app shows. Requires `hub:read`.
   * The read surface never carries a provider error body (only provider +
   * status_code + category + counts), so it's safe to print verbatim.
   */
  async getHubAlerts(hubId: string): Promise<HubAlertsListResponse> {
    return this.request<HubAlertsListResponse>('GET', `/api/hubs/${encodeURIComponent(hubId)}/alerts`);
  }

  /** Set a credential on a PREVIEW connection (direct secret or org-credential link). */
  async setPreviewConnectionCredential(connectionId: string, body: Record<string, unknown>): Promise<EditConnectionResponse> {
    return this.request('PATCH', `/api/setup/connections/${connectionId}`, body);
  }

  /** Set a credential on a PRODUCTION connection (the sanctioned carve-out). */
  async setProductionConnectionCredential(connectionId: string, body: Record<string, unknown>): Promise<SetProductionCredentialResponse> {
    return this.request('PATCH', `/api/setup/connections/${connectionId}/production-credential`, body);
  }

  async updateOrgCredential(
    credentialId: string,
    organizationId: string,
    body: UpdateOrgCredentialInput,
  ): Promise<UpdateOrgCredentialResponse> {
    const qs = new URLSearchParams({ organization_id: organizationId });
    return this.request('PUT', `/api/setup/organization-credentials/${credentialId}?${qs.toString()}`, body);
  }

  async listOrgTags(organizationId: string): Promise<ListOrgTagsResponse> {
    const qs = new URLSearchParams({ organization_id: organizationId });
    return this.request('GET', `/api/setup/organization-tags?${qs.toString()}`);
  }

  /**
   * Project an organization credential onto a base.
   *
   * WayAI's own setup API rather than the data proxy: the credential is WayAI's, and this
   * route is the only one that decrypts it, applies the tag/environment bind gate, and
   * records the link that makes a later rotation reach the base.
   */
  async linkOrgCredentialToBase(
    credentialId: string,
    body: { organization_id: string; base_id: string; name?: string },
  ): Promise<CreateBaseCredentialLinkResponse> {
    return this.request(
      'POST',
      `/api/setup/organization-credentials/${encodeURIComponent(credentialId)}/base-links`,
      body,
    );
  }

  /** Remove that projection and the link recording it. */
  async unlinkOrgCredentialFromBase(
    credentialId: string,
    baseCredentialId: string,
    query: { organization_id: string; base_id: string },
  ): Promise<{ success: boolean }> {
    const qs = new URLSearchParams(query);
    return this.request(
      'DELETE',
      `/api/setup/organization-credentials/${encodeURIComponent(credentialId)}`
        + `/base-links/${encodeURIComponent(baseCredentialId)}?${qs.toString()}`,
    );
  }

  async createHub(
    organizationId: string,
    opts: { hubName: string; hubType?: string; hubDescription?: string; previewLabel?: string | null },
  ): Promise<{
    data: Array<{
      hub_id: string;
      hub_name: string;
      hub_environment: string;
      hub_type: string;
      [key: string]: unknown;
    }>;
  }> {
    return this.request('POST', '/api/setup/hubs', {
      organization_id: organizationId,
      hub_name: opts.hubName,
      ...(opts.hubType && { hub_type: opts.hubType }),
      ...(opts.hubDescription && { hub_description: opts.hubDescription }),
      ...(opts.previewLabel != null && { preview_label: opts.previewLabel }),
    });
  }

  /**
   * Replicate a hub into a new sibling preview. The source may be a preview or a
   * production hub; the new preview links to the same production parent. Returns
   * the new preview hub id (POST /api/setup/hubs/:id/replicate-preview).
   */
  async replicatePreview(hubId: string, previewLabel?: string | null): Promise<{
    data: { preview_hub_id: string };
  }> {
    return this.request('POST', `/api/setup/hubs/${hubId}/replicate-preview`, {
      preview_label: previewLabel ?? null,
    });
  }

  /**
   * Preview → production diff — the source of truth for what `wayai publish`
   * will do. `data.is_first_publish` distinguishes a first publish (no linked
   * production hub yet, all entities listed as added) from a subsequent sync.
   * Requires only `hub:read`, so it also drives the pre-confirm preview.
   */
  async getHubDiff(hubId: string): Promise<GetHubDiffResponse> {
    return this.request<GetHubDiffResponse>('GET', `/api/setup/hubs/${hubId}/diff`);
  }

  /**
   * First publish: clone the preview hub into a NEW production hub. Errors if the
   * preview is already linked to production (use `syncHub` instead). Returns the
   * new production hub id (POST /api/setup/hubs/:id/publish — HTTP 201).
   */
  async publishHub(hubId: string): Promise<PublishHubResponse> {
    return this.request<PublishHubResponse>('POST', `/api/setup/hubs/${hubId}/publish`);
  }

  /**
   * Sync a preview hub's changes into its linked production hub
   * (POST /api/setup/hubs/:id/sync). No-op-safe when already in sync.
   */
  async syncHub(hubId: string): Promise<SyncHubResponse> {
    return this.request<SyncHubResponse>('POST', `/api/setup/hubs/${hubId}/sync`);
  }

  /**
   * Set or clear a preview hub's label (PATCH /api/setup/hubs/:id). Pass null to
   * clear. The backend trims and normalizes empty → null (max 60 chars).
   */
  async relabelPreview(hubId: string, previewLabel: string | null): Promise<{
    // The PATCH returns the full updated hub row (SELECT *), so the caller can
    // drive the local folder rename + hub.yaml edit without a second round-trip.
    data: Array<{
      hub_id: string;
      hub_name: string;
      hub_environment: string;
      preview_label?: string | null;
      branch_name?: string | null;
      [key: string]: unknown;
    }>;
  }> {
    return this.request('PATCH', `/api/setup/hubs/${hubId}`, {
      preview_label: previewLabel,
    });
  }

  // ---------------------------------------------------------------------------
  // Reports (platform_report triage queue)
  // ---------------------------------------------------------------------------

  async submitReport(params: {
    title: string;
    description: string;
    source?: string;
    classification?: string;
    dedup_key?: string;
    organization_id?: string;
    hub_id?: string;
    conversation_id?: string;
    agent_id?: string;
    agent_name?: string;
    error_message?: string;
    steps_to_reproduce?: string;
    additional_context?: string;
    client_version?: string;
  }): Promise<{
    report_id: string;
    status: string;
    created: boolean;
    /** Set when this arrival hit an already-terminal row — see CreateReportResponse. */
    recurrence?: { reopened: boolean };
  }> {
    return this.request('POST', '/api/reports', params);
  }

  /** Reporter content edit of an own `cli` report (PATCH /api/reports/:id). */
  async editReport(reportId: string, patch: EditReportBody): Promise<EditReportResponse> {
    return this.request('PATCH', `/api/reports/${encodeURIComponent(reportId)}`, patch);
  }

  /** Reporter read-back — own reports, newest first (GET /api/reports[?status=]). */
  async listMyReports(status?: string): Promise<ReporterListResponse> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request('GET', `/api/reports${qs}`);
  }

  /** Reporter read-back — one own report + its message thread (GET /api/reports/:id). */
  async getMyReport(reportId: string): Promise<GetReportResponse> {
    return this.request('GET', `/api/reports/${encodeURIComponent(reportId)}`);
  }

  /** Reporter accepts a shipped fix → addressed (POST /api/reports/:id/accept). */
  async acceptReport(reportId: string): Promise<ReporterTransitionResponse> {
    return this.request('POST', `/api/reports/${encodeURIComponent(reportId)}/accept`);
  }

  /** Reporter contests a fix or dismissal → contested (POST /api/reports/:id/contest). */
  async contestReport(reportId: string, reason: string): Promise<ReporterTransitionResponse> {
    return this.request('POST', `/api/reports/${encodeURIComponent(reportId)}/contest`, { reason });
  }

  async listAdminReports(params: {
    status?: string;
    source?: string;
    limit?: number;
    offset?: number;
    since?: string;
    resolved_since?: string;
  } = {}): Promise<AdminReportListResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.source) qs.set('source', params.source);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    if (params.since) qs.set('since', params.since);
    if (params.resolved_since) qs.set('resolved_since', params.resolved_since);
    const suffix = qs.toString();
    return this.request('GET', `/api/admin/reports${suffix ? '?' + suffix : ''}`);
  }

  async getAdminReport(reportId: string): Promise<{ data: AdminReportDetail }> {
    return this.request('GET', `/api/admin/reports/${encodeURIComponent(reportId)}`);
  }

  async transitionAdminReport(
    reportId: string,
    body: TransitionReportBody,
  ): Promise<TransitionReportResponse> {
    return this.request('PATCH', `/api/admin/reports/${encodeURIComponent(reportId)}/transition`, body);
  }

  async groupAdminReports(
    canonicalId: string,
    memberIds: string[],
  ): Promise<GroupReportsResponse> {
    return this.request('POST', `/api/admin/reports/${encodeURIComponent(canonicalId)}/group`, {
      member_ids: memberIds,
    });
  }

  async ungroupAdminReport(memberId: string): Promise<UngroupReportResponse> {
    return this.request('POST', `/api/admin/reports/${encodeURIComponent(memberId)}/ungroup`);
  }

  /** Hold a pending report one triage cycle (POST /api/admin/reports/:id/hold). Silent; internal note. */
  async holdAdminReport(reportId: string, reason: string): Promise<HoldReportResponse> {
    return this.request('POST', `/api/admin/reports/${encodeURIComponent(reportId)}/hold`, { reason });
  }

  /** Admin content edit of any report (PATCH /api/admin/reports/:id). */
  async updateAdminReport(reportId: string, patch: EditReportBody): Promise<EditReportResponse> {
    return this.request('PATCH', `/api/admin/reports/${encodeURIComponent(reportId)}`, patch);
  }

  // ---------------------------------------------------------------------------
  // People moderation (platform-admin only): person-to-person reports and the team's actions
  // ---------------------------------------------------------------------------

  async listAdminPeopleReports(params: AdminPeopleReportListQuery = {}): Promise<AdminPeopleReportListResponse> {
    const qs = new URLSearchParams();
    if (params.pending !== undefined) qs.set('pending', params.pending);
    if (params.since) qs.set('since', params.since);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString();
    return this.request('GET', `/api/admin/people/reports${suffix ? '?' + suffix : ''}`);
  }

  async getAdminPeopleReport(reportId: string): Promise<AdminPeopleReportResponse> {
    return this.request('GET', `/api/admin/people/reports/${encodeURIComponent(reportId)}`);
  }

  async dismissAdminPeopleReport(reportId: string, body: AdminPeopleNoteBody): Promise<AdminPeopleModerationResponse> {
    return this.request('POST', `/api/admin/people/reports/${encodeURIComponent(reportId)}/dismiss`, body);
  }

  async removeAdminPeopleMessage(body: AdminPeopleMessageRemovalBody): Promise<AdminPeopleModerationResponse> {
    return this.request('POST', '/api/admin/people/message-removals', body);
  }

  async getAdminPeopleUser(userId: string): Promise<AdminPeopleUserResponse> {
    return this.request('GET', `/api/admin/people/users/${encodeURIComponent(userId)}`);
  }

  async suspendAdminPeopleUser(userId: string, body: AdminPeopleSuspendBody): Promise<AdminPeopleModerationResponse> {
    return this.request('POST', `/api/admin/people/users/${encodeURIComponent(userId)}/suspend`, body);
  }

  async unsuspendAdminPeopleUser(userId: string, body: AdminPeopleNoteBody): Promise<AdminPeopleModerationResponse> {
    return this.request('POST', `/api/admin/people/users/${encodeURIComponent(userId)}/unsuspend`, body);
  }

  // ---------------------------------------------------------------------------
  // Platform notices (feed 2) — operator-authored broadcasts (platform-admin only)
  // ---------------------------------------------------------------------------

  async listAdminNotices(params: {
    status?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<AdminNoticeListResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const suffix = qs.toString();
    return this.request('GET', `/api/admin/notices${suffix ? '?' + suffix : ''}`);
  }

  async createAdminNotice(body: CreateNoticeBody): Promise<AdminNoticeResponse> {
    return this.request('POST', '/api/admin/notices', body);
  }

  // NOTE: no updateAdminNotice here — the PATCH /api/admin/notices/:id route exists
  // (CRUD-complete, tested) for the future /admin/notices authoring UI, which uses the
  // frontend client. The CLI exposes create/list/resolve only; add an update wrapper if
  // a `wayai admin notice update` subcommand is ever wired.
  async resolveAdminNotice(noticeId: string): Promise<AdminNoticeResponse> {
    return this.request('POST', `/api/admin/notices/${encodeURIComponent(noticeId)}/resolve`);
  }

  async getPendingSchedules(hubId: string, conversationId: string): Promise<{
    entries: Array<{
      conversation_schedule_id: string;
      schedule_type: string;
      run_at: string;
      message_text: string;
      followup_order: number | null;
      followup_source: string | null;
      end_conversation: boolean;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams({ hub_id: hubId, conversation_id: conversationId });
    return this.request('GET', `/api/conversations/schedules?${params.toString()}`);
  }

  // ---------------------------------------------------------------------------
  // Admin debug reads (platform-admin only — 403 for everyone else)
  // ---------------------------------------------------------------------------

  async debugListTables(
    doType: DataExplorerDebugDoType,
    doId: string,
  ): Promise<{
    data: { do_type: string; do_id: string; tables: string[] };
  }> {
    return this.request(
      'GET',
      `/api/admin/data-explorer/debug/do/${doType}/${encodeURIComponent(doId)}/tables`,
    );
  }

  async debugReadTable(
    doType: DataExplorerDebugDoType,
    doId: string,
    table: string,
    opts?: { limit?: number; id?: string },
  ): Promise<{
    data: {
      do_type: string;
      do_id: string;
      table: string;
      rows: Array<Record<string, unknown>>;
      redacted_columns: string[];
      truncated: boolean;
      limit: number;
    };
  }> {
    const params = new URLSearchParams();
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.id) params.set('id', opts.id);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request(
      'GET',
      `/api/admin/data-explorer/debug/do/${doType}/${encodeURIComponent(doId)}/tables/${encodeURIComponent(table)}/rows${qs}`,
    );
  }

  async debugListAnalyticsTables(): Promise<{
    data: { tables: DataExplorerDebugAnalyticsTable[] };
  }> {
    return this.request('GET', '/api/admin/data-explorer/debug/analytics/tables');
  }

  async debugReadAnalyticsTable(
    table: DataExplorerDebugAnalyticsTable,
    opts?: { conversationId?: string; hubId?: string; messageId?: string; limit?: number },
  ): Promise<{
    data: {
      table: DataExplorerDebugAnalyticsTable;
      rows: Array<Record<string, unknown>>;
      redacted_columns: string[];
      truncated: boolean;
      limit: number;
    };
  }> {
    const params = new URLSearchParams();
    if (opts?.conversationId) params.set('conversation_id', opts.conversationId);
    if (opts?.hubId) params.set('hub_id', opts.hubId);
    if (opts?.messageId) params.set('message_id', opts.messageId);
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request(
      'GET',
      `/api/admin/data-explorer/debug/analytics/${encodeURIComponent(table)}/rows${qs}`,
    );
  }

  async debugReadArchive(
    hubId: string,
    conversationId: string,
    opts?: { messageId?: string },
  ): Promise<{
    data: {
      hub_id: string;
      conversation_id: string;
      archive: Record<string, unknown>;
      message_filtered: boolean;
    };
  }> {
    const params = new URLSearchParams();
    if (opts?.messageId) params.set('message_id', opts.messageId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request(
      'GET',
      `/api/admin/data-explorer/debug/archive/${encodeURIComponent(hubId)}/conversations/${encodeURIComponent(conversationId)}${qs}`,
    );
  }

  /**
   * Download the harness sandbox FILESYSTEM archive (harness-agents PR 2.12) as
   * raw zip bytes. Distinct from the JSON `request<T>` path — the response is a
   * binary `application/zip`, so this reads `arrayBuffer()`. Single-refresh-on-401
   * like `request`, but no transient-retry loop (a large binary GET is idempotent
   * but not worth re-streaming on a cold-start body probe). 404 ⇒ ApiError (no
   * sandbox for this conversation, or the blob was purged).
   */
  async downloadArchiveSandboxFs(hubId: string, conversationId: string): Promise<Uint8Array> {
    const path = `/api/archive/${encodeURIComponent(hubId)}/conversations/${encodeURIComponent(conversationId)}/sandbox`;
    addApiBreadcrumb('GET', path);
    const url = `${this.apiUrl}${path}`;

    let response = await this.send(url, 'GET');
    if (response.status === 401 && this.onUnauthorized) {
      let refreshed: string | undefined;
      try {
        refreshed = await this.onUnauthorized();
      } catch {
        // Refresh failed — fall through and surface the original 401 below.
      }
      if (refreshed) {
        this.accessToken = refreshed;
        response = await this.send(url, 'GET');
      }
    }

    if (!response.ok) {
      throw new ApiError('GET', path, response.status, await response.text());
    }
    return new Uint8Array(await response.arrayBuffer());
  }

  /**
   * List per-message observability metadata summaries for a conversation (hub-scoped auth). The
   * hub is named so the backend checks the conversation's row names it; a request naming none is
   * answered 404.
   */
  async listConversationObservability(hubId: string, conversationId: string): Promise<ObservabilityListResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request(
      'GET',
      `/api/conversations/${encodeURIComponent(conversationId)}/observability?${params}`,
    );
  }

  /** Fetch the full observability record for a single message (hub-scoped auth; the hub is named as above). */
  async getMessageObservability(hubId: string, conversationId: string, messageId: string): Promise<ObservabilityResponse> {
    const params = new URLSearchParams({ hub_id: hubId });
    return this.request(
      'GET',
      `/api/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/observability?${params}`,
    );
  }

  async listArchivedConversationObservability(
    hubId: string,
    conversationId: string,
  ): Promise<ObservabilityListResponse> {
    return this.request(
      'GET',
      `/api/archive/${encodeURIComponent(hubId)}/conversations/${encodeURIComponent(conversationId)}/observability`,
    );
  }

  async getArchivedMessageObservability(
    hubId: string,
    conversationId: string,
    messageId: string,
  ): Promise<ObservabilityResponse> {
    return this.request(
      'GET',
      `/api/archive/${encodeURIComponent(hubId)}/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/observability`,
    );
  }

  /** Platform-admin debug read of LLM observability records (list or single via messageId). */
  async debugReadObservability(
    hubId: string,
    conversationId: string,
    opts?: { messageId?: string },
  ): Promise<DataExplorerDebugObservabilityResponse> {
    const params = new URLSearchParams();
    if (opts?.messageId) params.set('message_id', opts.messageId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request(
      'GET',
      `/api/admin/data-explorer/debug/observability/${encodeURIComponent(hubId)}/conversations/${encodeURIComponent(conversationId)}${qs}`,
    );
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
    contentType?: string,
  ): Promise<T> {
    addApiBreadcrumb(method, path);

    const url = `${this.apiUrl}${path}`;
    let refreshedOn401 = false;

    for (let retry = 0; ; retry++) {
      let response = await this.send(url, method, body, extraHeaders, contentType);

      // Single-refresh-then-retry on 401: a token that rotated/expired mid-command
      // (e.g. run-eval's long poll outliving a short-lived OAuth JWT) self-heals
      // rather than tearing down the command (gh #2605). One refresh per request,
      // independent of the transient-retry loop below.
      if (response.status === 401 && this.onUnauthorized && !refreshedOn401) {
        refreshedOn401 = true;
        let refreshed: string | undefined;
        try {
          refreshed = await this.onUnauthorized();
        } catch {
          // Refresh failed (e.g. SESSION_EXPIRED) — fall through and surface the
          // original 401 to the caller below.
        }
        if (refreshed) {
          this.accessToken = refreshed;
          response = await this.send(url, method, body, extraHeaders, contentType);
        }
      }

      if (response.ok) {
        // Read as text first: a 204 / empty success body is legal on the Data
        // surface's deletes, and `response.json()` throws on an empty body.
        const okBody = await response.text();
        return (okBody ? JSON.parse(okBody) : undefined) as T;
      }

      const errorBody = await response.text();

      // Honor the backend's retryable-transient contract: a ClickHouse Cloud
      // cold-start surfaces as a 503 with `details.retryable: true`, and its
      // withClickHouseQuery wake probe means a bounded retry succeeds once CH
      // warms (the CLI analog of the frontend's cold-start poll loop, gh #2883).
      //
      // Gated to GET so the retry is provably safe regardless of backend drift:
      // the backend only throws the retryable 503 from GET detail routes (archive
      // transcript, observability), while POST list/dashboard CH routes signal a
      // cold start with a `{ cold: true }` 200 instead (packages/core errors.ts).
      // Restricting to the idempotent method stops a future write-path 503 that
      // mistakenly carried `retryable: true` from silently double-sending a
      // mutation — the invariant is enforced here, not just asserted in a comment.
      if (method === 'GET' && retry < RETRYABLE_BACKOFF_MS.length && isRetryableErrorBody(errorBody)) {
        await delay(RETRYABLE_BACKOFF_MS[retry]);
        continue;
      }

      throw new ApiError(method, path, response.status, errorBody);
    }
  }

  /**
   * One request against the Data (`/v1`) surface — bases, records, files and the
   * rest of the Bases namespace.
   *
   * `v1Path` is the Data backend's OWN path, verbatim (`/v1/bases`), so every
   * ported command spells the path the Data Worker documents; only
   * `toDataProxyPath` knows how WayAI mounts that surface. Identity is the
   * caller's ordinary WayAI session — there is one keyring login, and the
   * backend attests the org over its service binding rather than forwarding a
   * credential.
   *
   * Returns the surface's `{ data, meta }` envelope rather than unwrapping, so
   * cursor-paginated lists can follow `meta.has_more`. A failure throws the same
   * `ApiError` as every other CLI request, so the top-level handler is unchanged;
   * `dataErrorMessage` recovers the envelope's own message for display.
   *
   * `orgId` is the caller's org SELECTOR (the `--org` flag, else the repo's
   * `wayai-ws/wayai.yaml`), not an authorization claim: the backend validates it against
   * the caller's grants and attests only the validated result. A multi-org user
   * needs it to pick; a single-org user can omit it.
   *
   * It travels as the proxy's own query param, whose name is imported rather
   * than spelled — the handler validates it and strips it before forwarding,
   * since it is WayAI's selector and not an input Rekor sees.
   */
  async dataRequest<T>(
    method: string,
    v1Path: string,
    body?: unknown,
    orgId?: string,
  ): Promise<DataEnvelope<T>> {
    const envelope = await this.request<DataEnvelope<T> | undefined>(
      method,
      withOrgSelector(toDataProxyPath(v1Path), orgId),
      body,
    );
    return envelope ?? {};
  }

  /**
   * Upload raw bytes to a Data `/v1` path — file content and record
   * attachments, the two places that surface takes a body which is not JSON.
   * `extraHeaders` carries the surface's own metadata headers, which are
   * caller-supplied and so cannot displace the org selector or anything `send`
   * pins.
   */
  async dataUpload<T>(
    v1Path: string,
    bytes: Uint8Array,
    contentType: string,
    orgId?: string,
    extraHeaders?: Record<string, string>,
  ): Promise<DataEnvelope<T>> {
    const envelope = await this.request<DataEnvelope<T> | undefined>(
      'PUT',
      withOrgSelector(toDataProxyPath(v1Path), orgId),
      bytes,
      extraHeaders,
      // NOT a header on the map above: `send` pins the content type out of
      // caller reach, and takes it here so it always describes the real body.
      contentType,
    );
    return envelope ?? {};
  }

  /**
   * Download a Data `/v1` path's raw bytes. Distinct from `dataRequest`, which
   * parses the JSON envelope: the response here is the stored blob itself, so
   * this reads `arrayBuffer()`. Single-refresh-on-401 like `request`, but no
   * transient-retry loop — re-streaming a blob is not worth a cold-start probe.
   */
  async dataDownload(
    v1Path: string,
    orgId?: string,
  ): Promise<{ bytes: Uint8Array; contentType: string }> {
    const path = withOrgSelector(toDataProxyPath(v1Path), orgId);
    addApiBreadcrumb('GET', path);
    const url = `${this.apiUrl}${path}`;

    let response = await this.send(url, 'GET');
    if (response.status === 401 && this.onUnauthorized) {
      let refreshed: string | undefined;
      try {
        refreshed = await this.onUnauthorized();
      } catch {
        // Refresh failed — fall through and surface the original 401 below.
      }
      if (refreshed) {
        this.accessToken = refreshed;
        response = await this.send(url, 'GET');
      }
    }

    if (!response.ok) {
      throw new ApiError('GET', path, response.status, await response.text());
    }
    return {
      bytes: new Uint8Array(await response.arrayBuffer()),
      contentType: response.headers.get('Content-Type') ?? 'application/octet-stream',
    };
  }

  /**
   * The absolute URL of a Data `/v1` path, for handing the user a link they can
   * fetch themselves. Only the address — every request the CLI issues goes
   * through the methods above, which carry the session.
   */
  dataUrl(v1Path: string, orgId?: string): string {
    // Carries the org selector like every other Data call. Without it the proxy
    // falls back to the caller's default org — which a platform admin does not
    // have (the request throws) and a multi-org user may not want, so the
    // printed link would fail where the CLI's own request succeeds.
    return `${this.apiUrl}${withOrgSelector(toDataProxyPath(v1Path), orgId)}`;
  }

  /** Issue a single authenticated request with the client's current token. */
  private send(
    url: string,
    method: string,
    body?: unknown,
    extraHeaders?: Record<string, string>,
    contentType?: string,
  ): Promise<Response> {
    return fetch(url, {
      method,
      headers: {
        // Caller-supplied FIRST, so everything pinned below is out of their
        // reach: a metadata header cannot restate the credential or mislabel
        // the body.
        ...extraHeaders,
        Authorization: `Bearer ${this.accessToken}`,
        // The content type describes THIS body, so it is derived from the body
        // rather than accepted from `extraHeaders` — which is pinned out above,
        // and would otherwise be the one way to mislabel a JSON request.
        // `||`, not `??`: an empty content type is not a content type, and an
        // unlabelled body should fall back rather than announce nothing.
        'Content-Type':
          body instanceof Uint8Array ? contentType || 'application/octet-stream' : 'application/json',
        // Names the calling surface for the Data Worker's audit rows, which
        // record `cli` vs `api` vs `mcp`. A HINT, not an
        // authorization input: any client can send it, so the backend must
        // normalize it against what it knows about its own ingress rather than
        // trust it. Nothing is granted or denied on its value; the Data Worker's
        // audit rows are its consumer.
        'X-WayAI-Client': 'cli',
      },
      // Bytes go on the wire verbatim (file content, attachments); everything
      // else is JSON. Encoding a Uint8Array through JSON.stringify would send
      // `{"0":137,…}` — a corrupt upload rather than a failed one.
      body: !body ? undefined : body instanceof Uint8Array ? body : JSON.stringify(body),
    });
  }
}
